// /opt/ptg01-v1/front-extranet/lib/TopBarOverrideContext.tsx
// Permet à une page de remplacer dynamiquement le bloc titre du TopBar global.
// Utilisation typique : afficher l'élément actuellement sélectionné (mission, CRA, dépense)
// directement dans le TopBar plutôt que d'empiler un second bandeau au-dessus des colonnes.
'use client';

import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';

export interface TopBarItem {
    title: string;
    subtitle?: string;
    badge?: { label: string; bgClass: string; textClass: string };
}

export interface TopBarOverride {
    eyebrow?: string;
    title?: string;
    /** Sous-titre optionnel rendu en dessous du titre (ex: "Lead Dev React · 650 €/j"). */
    subtitle?: string;
    /** Badge optionnel à droite du titre (ex: statut "Active"). */
    badge?: { label: string; bgClass: string; textClass: string };
    /** Mode multi-items : liste empilée verticalement (ex: 2 missions actives). */
    items?: TopBarItem[];
}

interface Ctx {
    override: TopBarOverride | null;
    setOverride: (o: TopBarOverride | null) => void;
}

const TopBarOverrideCtx = createContext<Ctx>({ override: null, setOverride: () => {} });

export function TopBarOverrideProvider({ children }: { children: React.ReactNode }) {
    const [override, setOverride] = useState<TopBarOverride | null>(null);
    const value = useMemo(() => ({ override, setOverride }), [override]);
    return <TopBarOverrideCtx.Provider value={value}>{children}</TopBarOverrideCtx.Provider>;
}

export function useTopBarOverride() {
    return useContext(TopBarOverrideCtx);
}

/** Hook : applique un override pendant la durée de vie du composant, le nettoie au démontage. */
export function useApplyTopBarOverride(override: TopBarOverride | null) {
    const { setOverride } = useTopBarOverride();
    // Sérialise pour éviter les re-renders en boucle
    const serialized = override ? JSON.stringify(override) : null;
    const memoizedSet = useCallback((valeur: TopBarOverride | null) => setOverride(valeur), [setOverride]);
    useEffect(() => {
        memoizedSet(override);
        return () => memoizedSet(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serialized, memoizedSet]);
}
